// Sanitização de Automações para exportar/importar entre clientes.
//
// O gatilho (`params.from`) e os passos (`params.to[]`) podem conter IDs
// vinculados a UM cliente (canal, funil, estágio, usuários, setores, tags,
// campanha, templates, mídias). Ao exportar esses IDs são zerados; ao importar
// a automação é criada INATIVA e os passos com campos limpos aparecem com borda
// vermelha no editor (ver stepValidation.js).
//
// i18n: funções puras (não componentes/hooks), então não chamam useTranslation()
// diretamente. Quem chama (Automacoes.js) passa o `t` obtido via
// useTranslation('automation'). Sem `t`, cai no fallback PT abaixo.

import { v4 as uuidv4 } from 'uuid';
import { stripFields } from 'src/utils/exportImport.utils';

// Fallback usado quando nenhum `t` é passado: apenas retorna o texto original.
const defaultT = (key, fallback) => fallback;

// União dos campos de cliente presentes no gatilho (`from`) e nos passos (`to[].data`).
export const AUTOMACAO_CLIENT_FIELDS = [
    // gatilho (from)
    'pipelineIdTarget',
    'stageIdTarget',
    'stage_ids',
    'chatChannels',
    'campaignId',
    'chatClientId',
    'chatUsersAccept',
    'actionTag',
    'valuesFields',
    'feedbacks',
    'tags',
    // passos (to[].data) — e alguns compartilhados com o gatilho
    'chatChannel',
    'pipelineId',
    'stageId',
    'chatUsersAssign',
    'chatDepartamentsAssign',
    'cardTransferUsers',
    'linkToChatUser',
    'chatTags',
    'chatTagsRemove',
    'chatFiles',
    'templateSelecionado',
    'templateVariaveis',
];

// Regenera os `id` dos passos (e sub-passos de condição) para evitar colisões.
function regenBlockIds(blocks) {
    return (blocks || []).map((b) => {
        const data = { ...(b.data || {}) };
        if (Array.isArray(data.truePath)) data.truePath = regenBlockIds(data.truePath);
        if (Array.isArray(data.falsePath)) data.falsePath = regenBlockIds(data.falsePath);
        return { ...b, id: uuidv4(), data };
    });
}

/**
 * Converte uma automação num objeto portável (sem IDs de cliente).
 * @param {object} a - automação vinda da listagem/detalhe
 * @param {(key: string, fallback: string) => string} [t] - função de tradução (i18next), opcional
 */
export function toPortableAutomacao(a, t = defaultT) {
    const params = stripFields(a?.params || {}, AUTOMACAO_CLIENT_FIELDS);
    if (Array.isArray(params.to)) {
        params.to = regenBlockIds(params.to);
    }
    return {
        name: a?.name || t('portable.defaultName', 'Automação importada'),
        params,
    };
}

/**
 * Infere quais bindings foram limpos, para avisar o usuário (labels amigáveis).
 * @param {object} data - objeto portável importado
 * @param {(key: string, fallback: string) => string} [t] - função de tradução (i18next), opcional
 * @returns {string[]}
 */
export function getAutomacaoResetWarnings(data, t = defaultT) {
    const found = new Set();
    const from = data?.params?.from;
    if (from) {
        // O gatilho pode ter tido canal/funil/etc. limpos; sinalizamos genericamente.
        found.add(t('portable.warnings.triggerGeneric', 'Gatilho: revise os filtros (canal, funil, etiquetas) se aplicável'));
    }
    const walk = (blocks) => {
        (blocks || []).forEach((b) => {
            switch (b.value) {
                case 'to.chat.sendMessage':
                    found.add(t('portable.warnings.sendMessage', 'Envio de mensagem: canal / mídia'));
                    break;
                case 'to.moveCardStage':
                case 'to.funnel.cardCreate':
                    found.add(t('portable.warnings.funnelStage', 'Funil e estágio'));
                    break;
                case 'to.chat.addTag':
                case 'to.chat.removeTag':
                    found.add(t('portable.warnings.tags', 'Etiquetas'));
                    break;
                case 'to.chat.transfer':
                case 'to.chat.assign':
                    found.add(t('portable.warnings.usersOrSectors', 'Usuários / setores'));
                    break;
                case 'to.funnel.cardTransferUser':
                    found.add(t('portable.warnings.transferUser', 'Usuário de transferência'));
                    break;
                default:
                    break;
            }
            if (Array.isArray(b.data?.truePath)) walk(b.data.truePath);
            if (Array.isArray(b.data?.falsePath)) walk(b.data.falsePath);
        });
    };
    walk(data?.params?.to);
    return [...found];
}
